import { useState, useEffect, useCallback } from "react";
import Button from "@common/Button";
import { useAuth } from "@context/AuthContext";
import { apiGetMyJobs, apiRemoveJob } from "@services/api";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import JobCard from "@features/jobs/JobCard";
import { Modal, ModalFooter } from "@common/Modal";

const PER_PAGE = 8;

// ── Job Modal (reused) ──
const emptyJob = { title: "", company: "", location: "", type: "Full-time", mode: "Remote", salary: "", skills: [], status: "Open", urgent: false };

const JobModal = ({ job, onClose, onSave }) => {
  const [form, setForm] = useState(job || emptyJob);
  const [skillInput, setSkillInput] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addSkill = (v) => { v = v.trim(); if (v && !form.skills.includes(v)) set("skills", [...form.skills, v]); setSkillInput(""); };
  const removeSkill = (s) => set("skills", form.skills.filter(x => x !== s));

  return (
    <Modal title={job?.id ? "Edit Job" : "Post a New Job"} onClose={onClose} maxWidth={560}
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={() => { if (form.title && form.company) onSave(form); }}
          confirmLabel={<><i className="fa-solid fa-floppy-disk" style={{fontSize:11}} /> {job?.id ? "Save Changes" : "Post Job"}</>}
        />
      }>
      <div className="row g-3">
        <div className="col-12">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Job Title *</label>
          <input className="form-control" value={form.title} onChange={e => set("title", e.target.value)} placeholder="Senior React Developer" style={{  }} />
        </div>
        <div className="col-6">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Company *</label>
          <input className="form-control" value={form.company} onChange={e => set("company", e.target.value)} placeholder="Deloitte" style={{  }} />
        </div>
        <div className="col-6">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Location</label>
          <input className="form-control" value={form.location} onChange={e => set("location", e.target.value)} placeholder="New York, USA" style={{  }} />
        </div>
        <div className="col-4">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Job Type</label>
          <select className="form-select" value={form.type} onChange={e => set("type", e.target.value)} style={{  }}>
            {["Full-time","Part-time","Contract","Freelance","Internship"].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-4">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Work Mode</label>
          <select className="form-select" value={form.mode} onChange={e => set("mode", e.target.value)} style={{  }}>
            {["Remote","Hybrid","On-site"].map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="col-4">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Status</label>
          <select className="form-select" value={form.status} onChange={e => set("status", e.target.value)} style={{  }}>
            {["Open","Closed","Draft"].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-12">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Salary Range</label>
          <input className="form-control" value={form.salary} onChange={e => set("salary", e.target.value)} placeholder="$8,000 — $12,000 / month" style={{  }} />
        </div>
        <div className="col-12">
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", display: "block", marginBottom: 4 }}>Required Skills</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
            {form.skills.map(s => (
              <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#2563EB" }}>
                {s}
                <button onClick={() => removeSkill(s)} style={{ background: "none", border: "none", cursor: "pointer", color: "#93C5FD", padding: 0, fontSize: 11 }}><i className="fa-solid fa-xmark" /></button>
              </span>
            ))}
          </div>
          <div className="input-group input-group-sm">
            <input className="form-control" placeholder="Type a skill and press Enter" value={skillInput}
              onChange={e => setSkillInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSkill(skillInput); } }}
              style={{  }} />
            <Button variant="secondary" size="md" onClick={() => addSkill(skillInput)}><i className="fa-solid fa-plus" /></Button>
          </div>
        </div>
        <div className="col-12">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="urgent-chk" checked={form.urgent || false} onChange={e => set("urgent", e.target.checked)} />
            <label className="form-check-label" htmlFor="urgent-chk" style={{ fontSize: 13 }}>Mark as Urgent</label>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ── Tabs (shared) ──
const JobTabs = ({ active }) => {
  const navigate = useNavigate();
  const tabs = [
    { key: "latest", label: "Latest Jobs",    icon: "fa-fire",          path: "/jobs/latest" },
    { key: "my",     label: "My Posted Jobs", icon: "fa-pen-to-square", path: "/jobs/my-posted-jobs" },
  ];
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => navigate(t.path)}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 18px", borderRadius: 9, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", background: active === t.key ? "#111827" : "#fff", color: active === t.key ? "#fff" : "#6B7280", boxShadow: active === t.key ? "none" : "inset 0 0 0 1.5px #E5E7EB", transition: "all 0.2s" }}>
          <i className={`fa-solid ${t.icon}`} style={{ fontSize: 12 }} />
          {t.label}
        </button>
      ))}
    </div>
  );
};

// ── My Posted Jobs Page ──
const MyPostedJobsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || 1);

  const [myJobs, setMyJobsState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fc_my_jobs") || "[]"); } catch { return []; }
  });
  const setMyJobs = (updater) => {
    setMyJobsState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("fc_my_jobs", JSON.stringify(next));
      return next;
    });
  };

  const [modal,     setModal]     = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [search,    setSearch]    = useState("");

  const filtered   = myJobs.filter(j => !search || j.title.toLowerCase().includes(search.toLowerCase()) || j.company.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const setPage = (p) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (p <= 1) next.delete("page"); else next.set("page", String(p));
      return next;
    }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = (form) => {
    if (form.id) {
      setMyJobs(j => j.map(x => x.id === form.id ? { ...form } : x));
    } else {
      setMyJobs(j => [...j, { ...form, id: Date.now(), posted: "Just now", applicants: 0 }]);
    }
    setModal(null); setActiveJob(null);
  };

  const handleDelete = (id) => setMyJobs(j => j.filter(x => x.id !== id));

  return (
    <div style={{  }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: 0 }}>Job Listings</h1>
          <p style={{ fontSize: 12, color: "#6B7280", margin: "3px 0 0" }}>{filtered.length} job{filtered.length !== 1 ? "s" : ""} posted</p>
        </div>
        <Button variant="primary" size="md" onClick={() => { setActiveJob(null); setModal("add"); }}>
          <i className="fa-solid fa-plus me-1" /> Post a Job
        </Button>
      </div>

      {/* Tabs */}
      <JobTabs active="my" />

      {/* Search */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
        <div style={{ position: "relative" }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: 13 }} />
          <input className="form-control form-control-sm" placeholder="Search your posted jobs..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ paddingLeft: 34 }} />
        </div>
      </div>

      {/* Jobs */}
      {filtered.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, padding: "60px 24px", textAlign: "center" }}>
          <i className="fa-solid fa-briefcase" style={{ fontSize: 36, color: "#D1D5DB", display: "block", marginBottom: 12 }} />
          <p style={{ fontSize: 15, fontWeight: 600, color: "#6B7280" }}>You haven't posted any jobs yet</p>
          <p style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 16 }}>Click Post a Job to add your first listing</p>
          <Button variant="primary" size="sm" onClick={() => { setActiveJob(null); setModal("add"); }}>
            <i className="fa-solid fa-plus me-1" /> Post a Job
          </Button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {paginated.map(job => (
              <div key={job.id} style={{ position: "relative" }}>
                <JobCard job={job} onViewDetails={j => navigate(`/jobs/${j.id}`)} />
                {/* Edit/Delete overlay */}
                <div style={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 6 }}>
                  <Button variant="ghost" size="sm"
                    onClick={() => { setActiveJob(job); setModal("edit"); }}>
                    <i className="fa-solid fa-pen" />
                  </Button>
                  <Button variant="soft-danger" size="sm"
                    onClick={() => handleDelete(job.id)}>
                    <i className="fa-solid fa-trash" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <span style={{ fontSize: 13, color: "#6B7280" }}>
                Showing <strong>{(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE, filtered.length)}</strong> of <strong>{filtered.length}</strong> jobs
              </span>
              <div style={{ display: "flex", gap: 5 }}>
                <button onClick={() => setPage(page - 1)} disabled={page === 1}
                  style={{ width: 34, height: 34, borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer", color: page === 1 ? "#D1D5DB" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="fa-solid fa-chevron-left" style={{ fontSize: 11 }} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i+1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    style={{ width: 34, height: 34, borderRadius: 8, border: p === page ? "none" : "1.5px solid #E5E7EB", background: p === page ? "#2563EB" : "#fff", color: p === page ? "#fff" : "#6B7280", fontSize: 13, fontWeight: p === page ? 700 : 400, cursor: "pointer" }}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(page + 1)} disabled={page === totalPages}
                  style={{ width: 34, height: 34, borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#fff", cursor: page === totalPages ? "not-allowed" : "pointer", color: page === totalPages ? "#D1D5DB" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="fa-solid fa-chevron-right" style={{ fontSize: 11 }} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {(modal === "add" || modal === "edit") && (
        <JobModal job={modal === "edit" ? activeJob : null} onClose={() => { setModal(null); setActiveJob(null); }} onSave={handleSave} />
      )}
    </div>
  );
};

export default MyPostedJobsPage;
